import classNames from 'classnames';
import React, { useCallback, useLayoutEffect, useRef } from 'react';

import styles from './switch-v2.module.css';
import Container from '@ui/container/container';

interface SwitchV2Props<T> {
  value: T;
  on: boolean;
  onClick: (value: T, on: boolean) => void;
  disabled?: boolean;
  loading?: boolean;
  stopPropagation?: boolean;
  paused?: boolean;
  onLabel?: React.ReactNode;
  offLabel?: React.ReactNode;
  fullWidth?: boolean;
  height?: number;
}

const SwitchV2 = <T,>({
  value,
  on,
  onClick,
  disabled = false,
  loading = false,
  stopPropagation = false,
  paused = false,
  onLabel = 'On',
  offLabel = 'Off',
  fullWidth = false,
  height = 32,
}: SwitchV2Props<T>) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const offLabelRef = useRef<HTMLDivElement>(null);
  const onLabelRef = useRef<HTMLDivElement>(null);
  const offLabelMeasureRef = useRef<HTMLSpanElement | null>(null);
  const onLabelMeasureRef = useRef<HTMLSpanElement | null>(null);

  useLayoutEffect(() => {
    const measure = () => {
      if (!buttonRef.current) return;
      buttonRef.current.style.setProperty('--switch-height', `${height}px`);
      if (fullWidth) {
        buttonRef.current.style.setProperty('--switch-width', `100%`);
        buttonRef.current.style.setProperty('--thumb-width-on', `50%`);
        buttonRef.current.style.setProperty('--thumb-width-off', `50%`);
      } else if (offLabelMeasureRef.current && onLabelMeasureRef.current) {
        const thumbWidthOn = onLabelMeasureRef.current.offsetWidth + height;
        const thumbWidthOff = offLabelMeasureRef.current.offsetWidth + height;
        const totalWidth = thumbWidthOn + thumbWidthOff;
        buttonRef.current.style.setProperty('--switch-width', `${totalWidth}px`);
        buttonRef.current.style.setProperty('--thumb-width-on', `${thumbWidthOn}px`);
        buttonRef.current.style.setProperty('--thumb-width-off', `${thumbWidthOff}px`);
      }
    };

    measure();

    let cancelled = false;
    document.fonts.ready.then(() => {
      if (!cancelled) measure();
    });
    return () => {
      cancelled = true;
    };
  }, [onLabel, offLabel, fullWidth, height]);

  const memoizedClick = useCallback(
    (e?: React.MouseEvent<HTMLButtonElement>) => {
      if (disabled) return;
      if (stopPropagation) {
        e?.stopPropagation();
      }
      onClick(value, !on);
    },
    [disabled, on, onClick, stopPropagation, value]
  );

  return (
    <>
      {!fullWidth && (
        <>
          <span
            ref={offLabelMeasureRef}
            style={{
              position: 'fixed',
              visibility: 'hidden',
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
              top: 0,
              left: 0,
            }}
            aria-hidden="true"
          >
            {offLabel}
          </span>
          <span
            ref={onLabelMeasureRef}
            style={{
              position: 'fixed',
              visibility: 'hidden',
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
              top: 0,
              left: 0,
            }}
            aria-hidden="true"
          >
            {onLabel}
          </span>
        </>
      )}
      <Container loading={loading} className="flex" borderRadius="100px" width="100%">
        <button
          ref={buttonRef}
          onClick={memoizedClick}
          className={classNames('clear-button', styles['switch-v2'], {
            [styles['on']]: on,
            [styles['isDisabled']]: disabled,
            [styles['paused']]: paused,
            [styles['fullWidth']]: fullWidth,
          })}
          disabled={paused || disabled || loading}
        >
          <div className={styles['thumb']} />
          <div className={styles['gutter']}>
            <div
              className={classNames(styles.labelRow || '', 'flex ai-center', {
                'jc-space-around': fullWidth,
                'flex-1': fullWidth,
              })}
            >
              <div ref={offLabelRef} className={classNames('S3 z-10', !fullWidth && styles.offLabelWrapper)}>
                {offLabel}
              </div>
              <div ref={onLabelRef} className={classNames('S3 z-10', !fullWidth && styles.onLabelWrapper)}>
                {onLabel}
              </div>
            </div>
          </div>
        </button>
      </Container>
    </>
  );
};

export default SwitchV2;
